/** @jsxImportSource @emotion/react */

import { Box, Breadcrumbs, Grid, Skeleton, Theme, css, useTheme } from '@mui/material'
import { appLink } from '@app/config/appLink'
import { useLocation } from 'react-router-dom'

export const GuideLayoutSkeleton = () => {
  const { pathname } = useLocation()
  const isHomePage = pathname === appLink.guidePage.path

  return (
    <Box css={outerContainer}>
      <GuideNavigationSkeleton />
      <Box css={innerWrapper}>
        {!isHomePage && (
          <Box css={sidebarContainer}>
            <Box css={sidebarBackground} />
            <Box css={stickyNav}>
              <GuideSidebarNavigationSkeleton />
            </Box>
          </Box>
        )}
        <Box component="main" css={mainContent}>
          <Box css={contentContainer}>
            <Box
              css={css`
                padding: 0 24px;
              `}
            >
              <GuideBreadcrumbsSkeleton />
              <Skeleton variant="rectangular" height={180} sx={{ my: 2 }} />
              <Box css={pageBg}>
                <Box css={gridWrap}>
                  <Grid container spacing={2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                        <Skeleton variant="rectangular" height={240} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const pageBg = css`
  width: 100%;
`

const gridWrap = css`
  padding: 24px;
`

const GuideNavigationSkeleton = () => {
  const theme = useTheme()
  return (
    <Box
      css={css`
        position: sticky;
        top: 0;
        z-index: 130;
        display: flex;
        height: 70px;
        padding: 0 20px 0 24px;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
        border-bottom: 1px solid #d6d6d6;
        background-color: ${theme.palette.white.main};
        backdrop-filter: saturate(180%) blur(6px);
      `}
    >
      <Box
        role="presentation"
        css={css`
          display: flex;
          align-items: center;
          column-gap: 32px;
          align-self: stretch;
        `}
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} variant="text" width={100} height={24} sx={{ borderRadius: 4 }} />
        ))}
      </Box>
      <Skeleton variant="rectangular" width={260} height={40} sx={{ borderRadius: 20 }} />
    </Box>
  )
}

const outerContainer = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  font-family: Inter;
`

const innerWrapper = css`
  position: relative;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  width: 100%;
  flex: 1 1 auto;
  justify-content: center;
  max-width: var(--max-w-8xl, 90rem);

  @media (min-width: 640px) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  @media (min-width: 1024px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  @media (min-width: 1280px) {
    padding-left: 3rem;
    padding-right: 3rem;
  }
`

const sidebarContainer = css`
  display: none;
  @media (min-width: 1024px) {
    display: block;
    position: relative;
    flex: none;
  }
`

const sidebarBackground = css`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 50vw;
  background-color: #ffffffff;

  @media (prefers-color-scheme: dark) {
    display: none;
  }
`

const stickyNav = css`
  position: sticky;
  top: 4.75rem;
  margin-left: -0.125rem;
  height: calc(100vh - 4.75rem);
  width: 16rem;
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: 4rem;
  padding-bottom: 4rem;
  padding-right: 2rem;
  padding-left: 0.125rem;

  @media (min-width: 1280px) {
    width: 18rem;
    padding-right: 4rem;
  }
`

const mainContent = css`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const contentContainer = css`
  flex: 1 1 auto;
`

type GuideSidebarNavigationSkeletonProps = {
  sections?: number
  linksPerSection?: number
  className?: string
}

export const GuideSidebarNavigationSkeleton: React.FC<GuideSidebarNavigationSkeletonProps> = ({
  sections = 2,
  linksPerSection = 4,
  className,
}) => {
  const theme = useTheme()
  return (
    <Box component="nav" css={navRoot} className={className}>
      <Box component="ul" role="list" css={spaceY9}>
        {Array.from({ length: sections }).map((_, sIdx) => (
          <Box component="li" key={sIdx}>
            <Box component="h2" css={sectionTitle}>
              <Skeleton
                variant="text"
                width={160 + (sIdx % 3) * 40}
                height={24}
                sx={{ bgcolor: theme.palette.action.hover, borderRadius: 4 }}
              />
            </Box>
            <Box component="ul" role="list" css={sectionList}>
              {Array.from({ length: linksPerSection }).map((_, lIdx) => (
                <Box component="li" key={lIdx} css={listItemRelative}>
                  <Box css={linkBlock(theme)}>
                    <Box css={dotPlaceholder(theme)} />
                    <Skeleton
                      variant="text"
                      width={120 + ((sIdx + lIdx) % 4) * 30}
                      height={22}
                      sx={{ bgcolor: theme.palette.action.hover, borderRadius: 4 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

const navRoot = css`
  font-size: 1rem;
  line-height: 1.5rem;
  @media (min-width: 1024px) {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
`

const spaceY9 = css`
  list-style: none;
  margin: 0;
  padding: 0;
  & > * + * {
    margin-top: 2.25rem;
  }
`

const sectionTitle = css`
  font-weight: 900;
  color: #0f172a;
  @media (prefers-color-scheme: dark) {
    color: #ffffff;
  }
`

const sectionList = css`
  list-style: none;
  font-size: 14px;
  margin: 0;
  padding: 0;
  margin-top: 0.5rem;
  border-left: 2px solid #f1f5f9;
  & > * + * {
    margin-top: 0.5rem;
  }
  @media (min-width: 1024px) {
    margin-top: 1rem;
    border-left-color: #e2e8f0;
    & > * + * {
      margin-top: 1rem;
    }
  }
  @media (prefers-color-scheme: dark) {
    border-left-color: #1e293b;
  }
`

const listItemRelative = css`
  position: relative;
`

const linkBlock = (theme: Theme) => css`
  display: block;
  width: 100%;
  position: relative;
  padding-left: ${theme.spacing(4)};
  line-height: 155%;
  font-weight: 600;
`

const dotPlaceholder = (theme: Theme) => css`
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: -0.25rem;
  height: 0.375rem;
  width: 0.375rem;
  transform: translateY(-50%);
  border-radius: 9999px;
  background-color: ${theme.palette.action.disabledBackground};
  opacity: 0.35;
`

interface BreadcrumbSkeletonProps {
  links?: number
}

const GuideBreadcrumbsSkeleton: React.FC<BreadcrumbSkeletonProps> = ({ links = 3 }) => (
  <Box
    css={css`
      height: 40px;
      width: 100%;
      background-color: white;
      justify-content: flex-start;
      align-items: center;
    `}
  >
    <Box
      css={css`
        margin: 0 auto;
        height: inherit;
        display: flex;
        align-items: center;
        max-width: 1184px;
      `}
      sx={{ display: { xs: 'none', md: 'flex' }, my: 2 }}
    >
      <Breadcrumbs aria-label="breadcrumb" separator="›">
        <Skeleton variant="text" width={60} height={24} />
        {Array.from({ length: links }).map((_, i) => (
          <Skeleton key={i} variant="text" width={80 + (i % 3) * 20} height={24} />
        ))}
      </Breadcrumbs>
    </Box>
  </Box>
)
